import { Suspense, useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { Sidebar } from './components/Sidebar';
import { SplitProvider } from './components/SplitView';
import { BackButton } from './components/BackButton';
import { AssistantWidget } from './components/AssistantWidget';
import { NotificationBell } from './components/NotificationBell';
import { HeaderActionsContext, HeaderTitleContext } from './components/HeaderActions';

// Per-route topbar text (page name + short descriptor), mirroring the mockup's
// topbar. Titles reuse the nav labels; subtitles are dedicated short lines.
const TOPBAR: Record<string, { title: string; sub?: string }> = {
  '/home': { title: 'nav.home', sub: 'topbar.homeSub' },
  '/contacts': { title: 'nav.contacts', sub: 'topbar.contactsSub' },
  '/calls': { title: 'nav.calls', sub: 'topbar.callsSub' },
  '/activity': { title: 'nav.activity' },
  '/calendar': { title: 'nav.calendar' },
  '/inngest': { title: 'nav.inngest' },
  '/assistants': { title: 'nav.assistants' },
  '/superdoc/assistants': { title: 'nav.assistants' },
  '/admin/specialties': { title: 'nav.specialties' },
  '/admin/services': { title: 'nav.services' },
  '/users': { title: 'nav.users' },
  '/general-settings': { title: 'nav.generalSettings' },
  '/specialist-config': { title: 'nav.myProfile' },
};

function topbarMeta(pathname: string): { title: string; sub?: string } | undefined {
  if (TOPBAR[pathname]) return TOPBAR[pathname];
  if (pathname.startsWith('/specialist-config/')) return TOPBAR['/specialist-config'];
  // Assistant workflow — /assistants/:id/workflow
  if (/^\/assistants\/[^/]+\/workflow$/.test(pathname))
    return { title: 'workflow.title', sub: 'nav.assistants' };
  return undefined;
}

export default function App() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const meta = topbarMeta(pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // DOM node for the top-bar's right-hand actions — pages portal controls here.
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLDivElement | null>(null);
  // DOM node for the top-bar's LEFT title area (beside the back arrow) — detail
  // pages with no static TOPBAR title portal their own title here.
  const [headerTitleEl, setHeaderTitleEl] = useState<HTMLDivElement | null>(null);
  // Stable reference — Sidebar's "close on route change" effect depends on this;
  // an inline arrow would change every render and slam the drawer shut the
  // instant it opens.
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    // Lock the shell to the viewport so the sidebar and main can scroll
    // independently. Page-level document scroll is intentionally disabled.
    <div className="h-dvh flex bg-background overflow-hidden">
      <Sidebar open={sidebarOpen} onClose={closeSidebar} />

      {/* Main column — fills the remaining width. Pages decide their own
       *  max-width by wrapping in `mx-auto max-w-*` if they want narrow layout
       *  (auth forms, ResourceForm); tables/lists use the full width. */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Thin top bar on every page — back arrow (top-left) + the hamburger
         *  toggle on mobile only. */}
        <header className="relative z-[52] min-h-12 px-4 py-2 flex items-center gap-2 border-b border-border bg-card shrink-0">
          <BackButton />
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden inline-flex items-center justify-center rounded p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          {meta ? (
            <div className="min-w-0">
              <div className="text-[15px] font-semibold leading-tight truncate">{t(meta.title)}</div>
              {meta.sub ? (
                <div className="text-xs text-muted-foreground leading-tight truncate">{t(meta.sub)}</div>
              ) : null}
            </div>
          ) : null}
          {/* Left title area — detail pages with no static title portal here so
           *  their title sits right next to the back arrow. */}
          <div ref={setHeaderTitleEl} className="flex min-w-0 items-center gap-2" />
          {/* Right-hand area — page-controlled actions portal first, then the
           *  always-visible notifications bell pinned to the far right (consistent
           *  across every page), with breathing room from the page actions. */}
          <div className="ml-auto flex min-w-0 items-center gap-2">
            <div ref={setHeaderActionsEl} className="flex min-w-0 items-center gap-2" />
            <NotificationBell />
          </div>
        </header>
        {/* SplitProvider takes over the scroll story — when no split is open
         *  it wraps the routed page in a single overflow-auto pane; when open
         *  it mounts two resizable panes (primary + detail), each with their
         *  own scroll. Pages call `useSplit()` to pop the detail panel open. */}
        <main className="flex-1 min-w-0 overflow-hidden">
          <HeaderActionsContext.Provider value={headerActionsEl}>
            <HeaderTitleContext.Provider value={headerTitleEl}>
              <SplitProvider>
                {/* Catches lazy-loaded route chunks (code-split pages) while
                    their JS downloads on first navigation. */}
                <Suspense
                  fallback={<div className="px-4 py-8 text-muted-foreground">{t('common.loading')}</div>}
                >
                  <Outlet />
                </Suspense>
              </SplitProvider>
            </HeaderTitleContext.Provider>
          </HeaderActionsContext.Provider>
        </main>
      </div>
      <AssistantWidget />
    </div>
  );
}
